import { type UseChatOptions, useChat } from '@ai-sdk/react';
import { type AssistantRuntime, type ThreadMessage, useAuiState } from '@assistant-ui/react';
import { AssistantChatTransport, type UseChatRuntimeOptions, useAISDKRuntime } from '@assistant-ui/react-ai-sdk';
import { encode } from '@toon-format/toon';
import { lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from 'ai';

import { formatErrorText, type ToolErrorResponse } from '#plugin-chat/lib/tool/errors/index.ts';
import { isToolErrorOutput, ToolOutputErrorSchema } from '#plugin-chat/lib/tool/output-types.ts';
import { createThreadHistoryStore, type StoredMessageRepository } from '#plugin-chat/lib/transport/localStorage.ts';

// Tool parts with output-error state have errorText
type ToolErrorPart = { state: 'output-error'; errorText: string };

/**
 * Creates a chat thread runtime with automatic tool execution and error handling.
 * Wraps AI SDK's useChat to detect tool output errors and mark them appropriately.
 */
export function useChatThreadRuntime({
  adapters,
  transport,
  toCreateMessage,
  ...chatOptions
}: UseChatRuntimeOptions<UIMessage> = {}): AssistantRuntime {
  const id = useAuiState(({ threadListItem }) => threadListItem.id);

  // Load persisted messages from localStorage as initial state for useChat.
  // React Compiler will memoize this based on `id` dependency.
  const historyStore = createThreadHistoryStore(window.localStorage, id);
  const stored = historyStore.load();
  const messages =
    stored.messages.length > 0 ? stored.messages.map((m) => m.message as unknown as UIMessage) : undefined;

  const chat = useChat({
    // UseChatRuntimeOptions extends UseChatOptions but with stricter generic constraints.
    // The spread works at runtime but TS can't infer the generic parameter compatibility.
    ...(chatOptions as Partial<UseChatOptions<UIMessage>>),
    id,
    transport,
    // Automatically send follow-up requests when assistant completes with tool calls,
    // unless tool execution was aborted (stop button clicked)
    sendAutomaticallyWhen: (options: { messages: UIMessage[] }): boolean => {
      // Check if the base condition is met (has completed tool calls)
      const hasCompletedToolCalls = lastAssistantMessageIsCompleteWithToolCalls(options);
      if (!hasCompletedToolCalls) {
        return false;
      }

      // Also check message state (belt and suspenders - for cases where state has propagated)
      // FIXME: align with updated error handling in AtlasChatTransport
      const lastMessage = options.messages.at(-1);
      if (lastMessage?.role === 'assistant') {
        const hasAbortError = lastMessage.parts.some((part) => {
          const isToolOutputError =
            typeof part === 'object' &&
            part !== null &&
            'state' in part &&
            part.state === 'output-error' &&
            'errorText' in part &&
            typeof (part as ToolErrorPart).errorText === 'string';
          if (!isToolOutputError) {
            return false;
          }
          const errorText = part.errorText.toLowerCase();
          return errorText.includes('aborted') || errorText.includes('cancelled');
        });

        if (hasAbortError) {
          console.debug('[shouldContinueAgentLoop] Stopping - found abort/cancelled in message parts');
          return false;
        }
      }

      return true;
    },
    messages,
    onFinish: ({ messages: allMessages }) => {
      const store = createThreadHistoryStore(window.localStorage, id);
      const repo: StoredMessageRepository = {
        headId: allMessages.at(-1)?.id ?? null,
        messages: allMessages.map((msg, i) => ({
          message: msg as unknown as ThreadMessage,
          parentId: i > 0 ? (allMessages.at(i - 1)?.id ?? null) : null,
        })),
      };
      store.save(repo);
    },
  });

  // Detect errors in tool outputs by inspecting structure, not substring content.
  //
  // Note: assistant-stream returns "Tool execution was cancelled." with isError: true,
  // but useAISDKRuntime doesn't pass isError to addToolResult, so we re-derive
  // the error state here. The structural predicate `isToolErrorOutput` is the
  // single source of truth for the failure envelope shape — see
  // `lib/tool/output-types.ts`. Cancellation strings and explicit
  // `state: 'output-error'` are handled inline here as runtime-layer concerns.
  const wrappedAddToolOutput: typeof chat.addToolOutput = async (output) => {
    const outputValue = output?.output;
    const isCancelledOutput = typeof outputValue === 'string' && outputValue.toLowerCase().includes('cancelled');
    const explicitErrorState = (output as { state?: string })?.state === 'output-error';
    const shouldMarkAsError = explicitErrorState || isCancelledOutput || isToolErrorOutput(outputValue);

    if (shouldMarkAsError) {
      // Render structured tool errors via the shared formatter so the markdown
      // shape (header + message + suggestions) stays in sync with what
      // `ToolExecutionError.toStructuredResponse()` produces. Strings and
      // unstructured objects fall back to verbatim / TOON encoding.
      const renderErrorText = (error: unknown): string => {
        if (typeof error === 'string') {
          return error;
        }
        const parsed = ToolOutputErrorSchema.safeParse(error);
        if (parsed.success) {
          return formatErrorText(parsed.data satisfies ToolErrorResponse);
        }
        return encode(error);
      };
      await chat.addToolOutput({
        tool: output.tool,
        toolCallId: output.toolCallId,
        state: 'output-error',
        errorText: renderErrorText(outputValue),
      });
      return;
    }
    await chat.addToolOutput(output);
  };

  // Shallow copy with overridden methods - avoids immer's deep proxy which fails on frozen arrays
  const wrappedChat = {
    ...chat,
    addToolOutput: wrappedAddToolOutput,
    addToolResult: wrappedAddToolOutput,
  };

  const runtime = useAISDKRuntime(wrappedChat, {
    adapters,
    ...(toCreateMessage && { toCreateMessage }),
  });

  // Two-way binding: transport needs runtime reference for bidirectional communication
  if (transport instanceof AssistantChatTransport) {
    transport.setRuntime(runtime);
  }

  return runtime;
}
